import {
  TextField as MuiTextField,
  Checkbox,
  FormControlLabel,
  Box,
  Slider,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { ReactNode, useState, useEffect } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Field,
  FieldType,
  RangeField,
  SelectField,
  FileField,
  BlockGridField,
  SingleColumnListField,
  TwoColumnListField,
} from "../../types/types.form";
import FileUpload from "../FileUpload/FileUpload";
import DateFieldRenderer from "../DateFieldRenderer/DateFieldRenderer";
import SearchableDropdown from "../SearchableDropdown/SearchableDropdown";
import BlockGridInput from "../BlockGridInput/BlockGridInput";
import styles from "./fieldRenderers.module.css";

type TFunc = (key: string) => string;

type FieldRenderer = (
  field: Field,
  value: any,
  setValue: (v: any) => void,
  error?: string,
  touched?: boolean,
  t?: TFunc,
  onBlur?: (e: React.FocusEvent<any>) => void,
  disabled?: boolean
) => ReactNode;

export const fieldRenderers: Record<FieldType, FieldRenderer> = {

    text: (field, value, setValue, error, touched, t = (s) => s, onBlur, disabled) => (
        <MuiTextField
            fullWidth
            name={field.name}
            label={t(field.label)}
            placeholder={field.placeholder ? t(field.placeholder) : undefined}
            value={value ?? ""}
            onChange={(e) => setValue(e.target.value)}
            onBlur={onBlur}
            disabled={disabled}
            error={Boolean(touched && error)}
            helperText={touched && error ? t(error!) : ""}
            multiline
            minRows={3}
        />
    ),

    string: (field, value, setValue, error, touched, t = (s) => s, onBlur, disabled) => (
      <MuiTextField
        fullWidth
        name={field.name}
        label={t(field.label)}
        placeholder={field.placeholder ? t(field.placeholder) : undefined}
        value={value ?? ""}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        error={Boolean(touched && error)}
        helperText={touched && error ? t(error!) : ""}
      />
    ),

   string_disabled: (field, value, setValue, error, touched, t = (s) => s, onBlur, disabled) => (
    <MuiTextField
      fullWidth
      name={field.name}
      label={t(field.label)}
      placeholder={field.placeholder ? t(field.placeholder) : undefined}
      value={value ?? ""}
      onChange={(e) => setValue(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      error={Boolean(touched && error)}
      helperText={touched && error ? t(error!) : ""}
    />
  ),
   double: (field, value, setValue, error, touched, t = (s) => s, onBlur, disabled) => (
    <MuiTextField
      fullWidth
      name={field.name}
      label={t(field.label)}
      placeholder={field.placeholder ? t(field.placeholder) : undefined}
      value={value ?? ""}
      onChange={(e) => setValue(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      type="number"
      error={Boolean(touched && error)}
      helperText={touched && error ? t(error!) : ""}
    />
  ),

  int: (field, value, setValue, error, touched, t = (s) => s, onBlur, disabled) => (
    <MuiTextField
      fullWidth
      name={field.name}
      label={t(field.label)}
      placeholder={field.placeholder ? t(field.placeholder) : undefined}
      value={value ?? ""}
      onChange={(e) => setValue(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      type="number"
      error={Boolean(touched && error)}
      helperText={touched && error ? t(error!) : ""}
    />
  ),

  password: (field, value, setValue, error, touched, t = (s) => s, onBlur, disabled) => {
    const [showPassword, setShowPassword] = useState(false);
    return (
      <MuiTextField
        fullWidth
        type={showPassword ? "text" : "password"}
        name={field.name}
        label={t(field.label)}
        placeholder={field.placeholder ? t(field.placeholder) : undefined}
        value={value ?? ""}
        onChange={(e) => setValue(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        error={Boolean(touched && error)}
        helperText={touched && error ? t(error!) : ""}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={t("togglePassword")}
                onClick={() => setShowPassword(!showPassword)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
                size="small"
                disabled={disabled}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  },

  textarea: (field, value, setValue, error, touched, t = (s) => s, onBlur, disabled) => (
    <MuiTextField
      fullWidth
      name={field.name}
      label={t(field.label)}
      placeholder={field.placeholder ? t(field.placeholder) : undefined}
      value={value ?? ""}
      onChange={(e) => setValue(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      error={Boolean(touched && error)}
      helperText={touched && error ? t(error!) : ""}
      multiline
      minRows={3}
    />
  ),

  date: (field, value, setValue, error, touched, t = (s) => s, onBlur, disabled) => (
    <DateFieldRenderer
      field={field}
      value={value}
      setValue={setValue}
      error={error}
      touched={touched}
      t={t}
      onBlur={onBlur}
      disabled={disabled}
    />
  ),

  select: (field, value, setValue, error, touched, t = (s) => s, _onBlur, disabled) => {
    const selectField = field as SelectField;
    
    const options = selectField.customOptions 
      ? selectField.customOptions.map(opt => ({
          label: opt.label, 
          value: opt.value,
        }))
      : selectField.options.map((opt) => ({
          label: t(opt), 
          value: opt,
        }));

    return (
      <SearchableDropdown
        label={t(field.label)}
        options={options}
        value={value ?? ""}
        onChange={setValue}
        disabled={disabled}
      />
    );
  },

  multiselect: (field, value, setValue, error, touched, t = (s) => s, _onBlur, disabled) => {
    const selectField = field as SelectField;
    
    const options = selectField.customOptions 
      ? selectField.customOptions.map(opt => ({
          label: opt.label,
          value: opt.value,
        }))
      : selectField.options.map((opt) => ({
          label: t(opt),
          value: opt,
        }));

    return (
      <SearchableDropdown
        label={t(field.label)}
        multiple
        options={options}
        value={value ?? []}
        onChange={setValue}
        disabled={disabled}
      />
    );
  },

  checkbox: (field, value, setValue, _err, _tch, t = (s) => s, _onBlur, disabled) => (
    <label className={styles.checkboxLabel}>
      <input
        type="checkbox"
        className={styles.customCheckbox}
        checked={value === true || value === 1}
        onChange={(e) => setValue(e.target.checked)}
        disabled={disabled}
      />
      <span className={styles.checkboxText}>{t(field.label)}</span>
    </label>
  ),

  bool: (field, value, setValue, _err, _tch, t = (s) => s, _onBlur, disabled) => (
    <label className={styles.checkboxLabel}>
      <input
        type="checkbox"
        className={styles.customCheckbox}
        checked={value === true || value === 1}
        onChange={(e) => setValue(e.target.checked)}
        disabled={disabled}
      />
      <span className={styles.checkboxText}>{t(field.label)}</span>
    </label>
  ),

  range: (field, value, setValue, _e, _t, t = (s) => s, _onBlur, disabled) => {
    const [localValue, setLocalValue] = useState(value ?? 0);
    useEffect(() => setLocalValue(value ?? 0), [value]);

    return (
      <Box sx={{ width: "100%", px: 1, opacity: disabled ? 0.6 : 1 }}>
        <Typography gutterBottom>
          {t(field.label)}: {localValue}%
        </Typography>
        <Slider
          value={localValue}
          disabled={disabled}
          onChange={(_, newValue) => setLocalValue(newValue as number)}
          onChangeCommitted={(_, newValue) => setValue(newValue)}
          min={(field as RangeField).min || 0}
          max={(field as RangeField).max || 100}
          step={(field as RangeField).step || 1}
          marks
          valueLabelDisplay="on"
        />
      </Box>
    );
  },

  file: (field, value, setValue, error, touched, t = (s) => s, _onBlur, disabled) => (
    <FileUpload
      value={value}
      onChange={setValue}
      error={error ? t(error) : undefined}
      touched={touched}
      accept={(field as FileField).accept}
      label={t(field.label)}
      disabled={disabled}
    />
  ),

  blockgrid: (field, value, setValue, error, touched, t = (s) => s, _onBlur, disabled) => {
    const blockGridField = field as BlockGridField;

    return (
      <BlockGridInput
        value={value || []}
        onChange={setValue}
        availableBlocks={blockGridField.blocks}
        gridColumns={blockGridField.gridColumns || 3}
        label={t(field.label)}
        disabled={disabled}
      />
    );
  },

  fk: () => null, 

  singlecolumnlist: (field, value, setValue, error, touched, t = (s) => s, _onBlur, disabled) => {
    const singleColumnField = field as SingleColumnListField;
    return (
      <div>
        <Typography>{t(field.label)} (Single Column List - Coming Soon)</Typography>
      </div>
    );
  },

  twocolumnlist: (field, value, setValue, error, touched, t = (s) => s, _onBlur, disabled) => {
    const twoColumnField = field as TwoColumnListField;
    return (
      <div>
        <Typography>{t(field.label)} (Two Column List - Coming Soon)</Typography>
      </div>
    );
  },

  custom: () => null,
};